import type { CollectionId } from '@platewell/core';
import type { ListSource } from '@platewell/query';
import { createRootRoute, createRoute, createRouter, redirect } from '@tanstack/react-router';
import { AdminShell } from './admin-shell.js';
import { AppShell } from './app-shell.js';
import { RootLayout } from './root-layout.js';
import { AcceptInvite } from './routes/accept-invite.js';
import { AdminHousehold } from './routes/admin-household.js';
import { AdminHouseholds } from './routes/admin-households.js';
import { AdminUsers } from './routes/admin-users.js';
import { CollectionPage } from './routes/collection.js';
import { CollectionsManage } from './routes/collections-manage.js';
import { ImportSessionPage } from './routes/import-session.js';
import { ImportShare } from './routes/import-share.js';
import { ImportsPage } from './routes/imports.js';
import { RecipeDetail } from './routes/recipe-detail.js';
import { RecipeEdit } from './routes/recipe-edit.js';
import { RecipeList } from './routes/recipe-list.js';
import { FavouritesPage } from './routes/favourites.js';
import { RecentRecipesPage } from './routes/recent.js';
import { RecipeNew } from './routes/recipe-new.js';
import { Profile } from './routes/profile/index.js';
import { SchedulePage } from './routes/schedule.js';
import { Settings } from './routes/settings/index.js';
import { Subscribe } from './routes/subscribe.js';
import { Trash } from './routes/trash.js';

export const rootRoute = createRootRoute({ component: RootLayout });

/**
 * Pathless layout route: it contributes no URL segment, so every screen below keeps the
 * exact path it had when AppShell was the root component. It exists purely so the admin
 * console can sit alongside the app shell rather than inside it.
 */
const appLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  component: AppShell,
});

export interface IndexSearch {
  q?: string;
  tag?: string;
}

/** Context carried onto a recipe so it can page through, and return to, the list it came from. */
export interface RecipeSearch extends IndexSearch {
  collectionId?: string;
  /** Lists that have no filter params of their own, and so need naming explicitly. */
  from?: ListSource;
}

export interface ScheduleSearch {
  y?: number;
  m?: number;
}

export const loginRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/login',
  beforeLoad: () => {
    throw redirect({ to: '/' });
  },
});

// The marketing site links straight at /register; the signed-out screen renders the right form
// from the path, and a visitor who is already signed in belongs on their recipes instead.
export const registerRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/register',
  beforeLoad: () => {
    throw redirect({ to: '/' });
  },
});

export const forgotPasswordRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/forgot-password',
  beforeLoad: () => {
    throw redirect({ to: '/' });
  },
});

function parseIndexSearch(search: Record<string, unknown>): IndexSearch {
  return {
    q: typeof search.q === 'string' && search.q !== '' ? search.q : undefined,
    tag: typeof search.tag === 'string' && search.tag !== '' ? search.tag : undefined,
  };
}

function parseRecipeSearch(search: Record<string, unknown>): RecipeSearch {
  return {
    ...parseIndexSearch(search),
    collectionId:
      typeof search.collectionId === 'string' && search.collectionId !== ''
        ? (search.collectionId as CollectionId)
        : undefined,
    // A hand-typed value is dropped rather than rendering a back link to nowhere.
    from:
      search.from === 'all' || search.from === 'favourites' || search.from === 'recent'
        ? search.from
        : undefined,
  };
}

export const indexRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/',
  component: RecipeList,
  validateSearch: parseIndexSearch,
});

export const favouritesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/favourites',
  component: FavouritesPage,
});

export const recentRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/recent',
  component: RecentRecipesPage,
});

// Declared before the dynamic collection route so "manage" is never parsed as an id.
export const collectionsManageRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/collections/manage',
  component: CollectionsManage,
});

export const collectionRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/collections/$collectionId',
  component: CollectionPage,
});

export interface RecipeNewSearch {
  /** The collection the new recipes are filed into, when the page was opened from one. */
  collectionId?: CollectionId;
}

// Declared before the dynamic recipe route for readability; the router itself always
// prefers the static segment, so "new" can never be parsed as a recipe id.
export const recipeNewRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/recipes/new',
  component: RecipeNew,
  validateSearch: (search: Record<string, unknown>): RecipeNewSearch => ({
    collectionId:
      typeof search.collectionId === 'string' && search.collectionId !== ''
        ? (search.collectionId as CollectionId)
        : undefined,
  }),
});

export const recipeRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/recipes/$recipeId',
  component: RecipeDetail,
  validateSearch: parseRecipeSearch,
});

export const recipeEditRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/recipes/$recipeId/edit',
  component: RecipeEdit,
  // Editing keeps the list context so Done returns to a recipe that still knows its list.
  validateSearch: parseRecipeSearch,
});

export const trashRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/trash',
  component: Trash,
});

export const scheduleRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/schedule',
  component: SchedulePage,
  validateSearch: (search: Record<string, unknown>): ScheduleSearch => {
    const y = typeof search.y === 'number' ? search.y : Number(search.y);
    const m = typeof search.m === 'number' ? search.m : Number(search.m);
    const yearOk = Number.isInteger(y) && y >= 1970 && y <= 2100;
    const monthOk = Number.isInteger(m) && m >= 1 && m <= 12;
    if (yearOk && monthOk) return { y, m };
    return {};
  },
});

export type ProfileTab = 'account' | 'security';

export interface ProfileSearch {
  tab?: ProfileTab;
}

/**
 * The same tab-in-the-URL arrangement as /settings, and for the same reasons: `account`
 * is the default and is never written out, and availability is settled at render time
 * because `validateSearch` can see neither the session nor whether it is impersonated.
 */
export const profileRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/profile',
  component: Profile,
  validateSearch: (search: Record<string, unknown>): ProfileSearch =>
    search.tab === 'security' ? { tab: 'security' } : {},
});

export type SettingsTab = 'general' | 'collections' | 'household' | 'subscription';

export interface SettingsSearch {
  tab?: SettingsTab;
}

/**
 * The open tab, in the URL so it can be linked, reloaded and gone Back from.
 *
 * `general` is the default and is never written out, the same way an unset admin sort
 * key is dropped — a plain /settings stays clean.
 */
export const settingsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings',
  component: Settings,
  validateSearch: (search: Record<string, unknown>): SettingsSearch => {
    const tab = search.tab;
    return tab === 'collections' || tab === 'household' || tab === 'subscription' ? { tab } : {};
  },
});

export const subscribeRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/subscribe',
  component: Subscribe,
});

export const importRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/import/$token',
  component: ImportShare,
});

export const importsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/imports',
  component: ImportsPage,
});

export interface ImportSessionSearch {
  /** The collection the run files its recipes into, when it was started from one. */
  collectionId?: CollectionId;
}

export const importSessionRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/imports/$importId',
  component: ImportSessionPage,
  validateSearch: (search: Record<string, unknown>): ImportSessionSearch => ({
    collectionId:
      typeof search.collectionId === 'string' && search.collectionId !== ''
        ? (search.collectionId as CollectionId)
        : undefined,
  }),
});

export const inviteRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/invite/$token',
  component: AcceptInvite,
});

/**
 * The support console. AdminShell replaces the app sidebar entirely and does not wait on
 * the CRDT, so an admin with no household of their own can still use it.
 *
 * There is deliberately no `beforeLoad` permission check: it runs outside React and would
 * have to read the cached session, which is exactly the value that can be stale. AdminShell
 * gates on the live session instead, and the API's 403 is the guard that actually matters.
 */
const adminLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin',
  component: AdminShell,
});

const adminIndexRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/admin/households' });
  },
});

export interface AdminListSearch {
  page?: number;
  sort?: string;
  dir?: 'asc' | 'desc';
  q?: string;
}

/**
 * Defaults are dropped rather than written out, so a plain /admin/users URL stays clean and
 * "reset to page 1" is expressible as `page: undefined`. An unrecognised sort key is
 * discarded here as well as on the server, so the arrow never points at a column that is
 * not actually being sorted.
 */
function adminSearchParser(sorts: readonly string[]) {
  return (search: Record<string, unknown>): AdminListSearch => {
    const page = Number(search.page);
    return {
      page: Number.isInteger(page) && page >= 2 ? page : undefined,
      sort:
        typeof search.sort === 'string' && sorts.includes(search.sort) ? search.sort : undefined,
      dir: search.dir === 'asc' || search.dir === 'desc' ? search.dir : undefined,
      q: typeof search.q === 'string' && search.q !== '' ? search.q : undefined,
    };
  };
}

export const adminUsersRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'users',
  component: AdminUsers,
  validateSearch: adminSearchParser(['name', 'household', 'lastActive', 'createdAt']),
});

export const adminHouseholdsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'households',
  component: AdminHouseholds,
  validateSearch: adminSearchParser(['name', 'members', 'recipes', 'createdAt']),
});

/**
 * One household in full.
 *
 * It carries the list's own search params so Back returns to the page, sort and query the
 * agent left rather than to a reset list. They are parsed with the same parser as the list
 * for exactly that reason — anything the list would not accept must not survive the round
 * trip either.
 */
export const adminHouseholdRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'households/$householdId',
  component: AdminHousehold,
  validateSearch: adminSearchParser(['name', 'members', 'recipes', 'createdAt']),
});

const routeTree = rootRoute.addChildren([
  appLayoutRoute.addChildren([
    loginRoute,
    registerRoute,
    forgotPasswordRoute,
    indexRoute,
    favouritesRoute,
    recentRoute,
    collectionsManageRoute,
    collectionRoute,
    recipeNewRoute,
    recipeRoute,
    recipeEditRoute,
    trashRoute,
    scheduleRoute,
    profileRoute,
    settingsRoute,
    subscribeRoute,
    importsRoute,
    importSessionRoute,
    importRoute,
    inviteRoute,
  ]),
  adminLayoutRoute.addChildren([
    adminIndexRoute,
    adminUsersRoute,
    adminHouseholdsRoute,
    adminHouseholdRoute,
  ]),
]);

export const router = createRouter({ routeTree, defaultPreload: 'intent' });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
